import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import {
  transformRegionToMap,
  useConfig,
  useCookie,
  useSfScApi
} from 'msp-integrations'
import { ScrollTo } from 'msp-components'
import getCancellationFields from './CancellationForm.fields'
import FormStep from './FormStep'
import ReviewStep from './ReviewStep'
import SuccessModal from './SuccessModal'

const CONTACT_REASON = 'Bestellung und Lieferung'
const CONTACT_REASON_2 = 'Anfrage Stornierung'
const WEB_ORIGIN_URL = 'Widerruf'

type Step = 'form' | 'review'

const CancellationForm = ({
  data,
  regions = []
}: {
  data?: any
  regions?: any
}) => {
  const sfscApi = useSfScApi()
  const { eventTracking } = useCookie()
  const {
    ssr: { serviceCloudKeys },
    theme: { mspComponents }
  } = useConfig() as any
  const form = useForm()
  const fieldConfig = getCancellationFields()
  const headlineRegion = transformRegionToMap(regions)?.get('formHeadline')
  const [step, setStep] = useState<Step>('form')
  const [isSuccessOpen, setIsSuccessOpen] = useState(false)
  const [hasSubmitError, setHasSubmitError] = useState(false)

  const onPrepare = () => setStep('review')

  const onConfirm = async () => {
    setHasSubmitError(false)
    const formData = form.getValues()
    const formObj: Record<string, string> = {
      orgid: serviceCloudKeys?.contactFormKey,
      retURL: window.location.href,
      Web_Origin_URL__c: WEB_ORIGIN_URL,
      Contact_Reason__c: CONTACT_REASON,
      Contact_Reason_2__c: CONTACT_REASON_2,
      [mspComponents?.contactForm?.reasonFieldName || '00N1t00000LUmnD']:
        'Widerruf',
      W2C_First_Name__c: formData.firstName,
      W2C_E2C_Last_Name__c: formData.lastName,
      SFCC_Order_No__c: formData.orderNumber,
      email: formData.email,
      description: formData.description ?? ''
    }

    try {
      const response = await sfscApi.webToCase.send({
        body: new URLSearchParams(formObj).toString()
      })

      if (!response?.ok) {
        setHasSubmitError(true)
        return
      }

      form.reset(
        Object.fromEntries(Object.keys(fieldConfig).map((name) => [name, '']))
      )
      setStep('form')
      setIsSuccessOpen(true)
      eventTracking('formsSubmit', { gtm: { elementId: 'cancellation-form' } })
    } catch {
      setHasSubmitError(true)
    }
  }

  const onBack = () => {
    setHasSubmitError(false)
    setStep('form')
  }

  const wrapperClass = twMerge(
    'mx-auto w-full max-w-2xl px-4 py-8',
    data?.wrapperClassName
  )

  return (
    <>
      <div
        key={step}
        className={`${wrapperClass} animate-step-fade-in`}
        data-cy='cancellation-form-component'
      >
        <ScrollTo to='top' />
        {step === 'form' && (
          <FormStep
            form={form}
            fieldConfig={fieldConfig}
            headlineRegion={headlineRegion}
            introText={data?.introText}
            onPrepare={onPrepare}
          />
        )}
        {step === 'review' && (
          <ReviewStep
            values={form.getValues()}
            fieldConfig={fieldConfig}
            isSubmitting={form.formState.isSubmitting}
            hasError={hasSubmitError}
            onBack={onBack}
            onConfirm={form.handleSubmit(onConfirm)}
          />
        )}
      </div>
      <SuccessModal
        isOpen={isSuccessOpen}
        onClose={() => setIsSuccessOpen(false)}
        successMessage={data?.successMessage}
      />
    </>
  )
}

export default CancellationForm
